import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Download, FileText, BarChart3, Calendar, Eye } from "lucide-react";
import Layout from "../components/Layout";
import StatCounter from "../components/StatCounter";
import { useAuth } from "../context/AuthContext";
import { IMAGES } from "../lib/images";

const MILESTONES = [
  { date: "Q2 2026", title: "Capital formation closing", status: "On Track" },
  { date: "Q4 2026", title: "Site assembly & entitlements", status: "Active" },
  { date: "Q2 2027", title: "Construction commencement", status: "Scheduled" },
  { date: "Q3 2031", title: "Topping out — Elliott Towers", status: "Forecast" },
  { date: "Q4 2032", title: "Elliott Towers delivery", status: "Forecast" },
];

const REPORTS = [
  { name: "Q4 2025 Investor Report", date: "Jan 22, 2026", size: "2.4 MB" },
  { name: "Elliott Towers Project Briefing", date: "Nov 08, 2025", size: "6.1 MB" },
  { name: "Pipeline Update — Bayshore", date: "Sep 14, 2025", size: "1.8 MB" },
  { name: "Tampa Market Outlook 2026", date: "Jul 30, 2025", size: "3.2 MB" },
];

export default function InvestorDashboard() {
  const { user } = useAuth();

  return (
    <Layout>
      <section className="pt-32 pb-12 px-6 lg:px-12">
        <div className="max-w-[1600px] mx-auto">
          <div className="overline mb-3">— Investor Portal</div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-7">
              <h1 className="font-serif text-4xl md:text-6xl leading-[1.0] font-light tracking-tight">
                Welcome, <em className="text-primary italic">{user?.name?.split(" ")[0] || "Investor"}.</em>
              </h1>
              <p className="mt-4 text-muted-foreground">
                {user?.company || "Demo Investor"} · {user?.role === "admin" ? "Administrator" : "Investor"}
              </p>
            </div>
            <div className="lg:col-span-5 flex justify-end gap-3">
              {user?.role === "admin" && (
                <Link to="/admin" data-testid="dashboard-admin-link" className="inline-flex items-center gap-2 text-primary border border-primary/40 px-5 py-3 text-[12px] uppercase tracking-[0.2em] hover:bg-primary hover:text-primary-foreground transition-colors">
                  Admin Console
                </Link>
              )}
              <Link to="/elliott-towers" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-3 text-[12px] uppercase tracking-[0.2em] hover:bg-primary/90 transition-colors">
                Elliott Towers <ArrowUpRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PORTFOLIO STATS */}
      <section className="border-y border-primary/10 py-16 px-6 lg:px-12 bg-card">
        <div className="max-w-[1600px] mx-auto grid grid-cols-2 lg:grid-cols-4 gap-10">
          <StatCounter value={2} prefix="$" suffix=".5M" label="Committed Capital" />
          <StatCounter value={18} suffix="%" label="Target IRR" />
          <StatCounter value={2} suffix=".1x" label="Target Multiple" />
          <StatCounter value={4} label="Active Positions" />
        </div>
      </section>

      {/* PROJECT TRACKER */}
      <section className="py-20 lg:py-28 px-6 lg:px-12">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Flagship card */}
          <div className="lg:col-span-7 relative overflow-hidden glass">
            <div className="aspect-[16/9] overflow-hidden">
              <img src={IMAGES.elliott_twin_towers} alt="Elliott Towers" className="w-full h-full object-cover" />
            </div>
            <div className="p-8 lg:p-10">
              <div className="overline mb-3">— Flagship</div>
              <h2 className="font-serif text-3xl md:text-5xl tracking-tight">Elliott Towers</h2>
              <div className="mt-6 grid grid-cols-3 gap-6">
                <div><div className="font-mono text-2xl text-primary">38%</div><div className="overline mt-1 text-foreground/50">Capitalized</div></div>
                <div><div className="font-mono text-2xl text-primary">$1.1B</div><div className="overline mt-1 text-foreground/50">Pipeline Value</div></div>
                <div><div className="font-mono text-2xl text-primary">2032</div><div className="overline mt-1 text-foreground/50">Delivery</div></div>
              </div>
              <div className="mt-6 w-full h-1 bg-input">
                <div className="h-full bg-primary" style={{ width: "38%" }} />
              </div>
            </div>
          </div>

          {/* Milestones */}
          <div className="lg:col-span-5 glass p-8 lg:p-10">
            <Calendar className="w-6 h-6 text-primary mb-6" />
            <div className="overline mb-3">— Milestones</div>
            <h3 className="font-serif text-2xl tracking-tight">Project schedule</h3>
            <div className="mt-6 space-y-4">
              {MILESTONES.map((m) => (
                <div key={m.date} data-testid={`milestone-${m.date.replace(/\s+/g, "-")}`} className="flex items-start gap-4 pb-4 border-b border-primary/10 last:border-0">
                  <div className="font-mono text-xs text-primary w-20 mt-1">{m.date}</div>
                  <div className="flex-1">
                    <div className="text-sm">{m.title}</div>
                    <div className="overline mt-1 text-foreground/50">{m.status}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* REPORTS + QUICK ACTIONS */}
      <section className="py-20 lg:py-28 px-6 lg:px-12 bg-card border-y border-primary/10">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8">
            <FileText className="w-6 h-6 text-primary mb-6" />
            <div className="overline mb-3">— Reports & Documents</div>
            <h3 className="font-serif text-3xl tracking-tight">Confidential investor materials</h3>
            <div className="mt-8 divide-y divide-primary/10 border-t border-primary/10">
              {REPORTS.map((r) => (
                <div key={r.name} data-testid={`report-${r.name.slice(0, 24)}`} className="group flex items-center justify-between py-5 hover:bg-background/40 transition-colors px-2 -mx-2">
                  <div>
                    <div className="font-serif text-lg">{r.name}</div>
                    <div className="font-mono text-xs text-foreground/50 mt-1">{r.date} · {r.size}</div>
                  </div>
                  <button data-testid={`report-download-${r.name.slice(0, 16)}`} className="p-3 border border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground transition-colors">
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-4 space-y-4">
            <div className="glass p-8">
              <BarChart3 className="w-6 h-6 text-primary mb-4" />
              <div className="overline mb-2">— Quarterly Performance</div>
              <h4 className="font-serif text-xl">Q4 2025 distribution issued</h4>
              <p className="mt-3 text-sm text-muted-foreground">$48,200 distributed across positions. View statement below.</p>
              <button className="mt-5 text-[12px] uppercase tracking-[0.2em] text-primary hover:underline">View Statement</button>
            </div>
            <div className="glass p-8">
              <Eye className="w-6 h-6 text-primary mb-4" />
              <div className="overline mb-2">— Virtual Tour</div>
              <h4 className="font-serif text-xl">Elliott Towers immersive walkthrough</h4>
              <p className="mt-3 text-sm text-muted-foreground">An immersive WebGL preview of branded residences and amenities.</p>
              <button className="mt-5 text-[12px] uppercase tracking-[0.2em] text-primary hover:underline">Launch Tour</button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
